import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { FullScreenSpinner } from '@/components/core/common/feedback/FullScreenSpinner';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Overrides the default spinner for a route with a better-matched skeleton. */
  fallback?: ReactNode;
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  fallback,
  redirectTo = '/auth?mode=login',
}: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // While the auth check is in flight.
  //
  // This used to be a bordered `Card` with "Loading..." inside it, written out
  // twice - byte-identical here and in `PublicRoute`. `FullScreenSpinner`'s own
  // docstring already named that card as the thing it exists to replace, but
  // only `/onboarding` passed it, so every other route still flashed the card
  // before whatever it was guarding. It is the default now.
  if (loading) return <>{fallback ?? <FullScreenSpinner />}</>;

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
}
